import React from 'react';
import { Modal, Form, Input, Radio, Select, AutoComplete, Button, message } from 'antd';
import { Prospect, TProspectInsurance, Meta, TCarrier } from 'types/common';
import { updateProspect } from 'api/prospect';
import { gpUpdateProspect } from 'api/group';
import { FormInsurance } from 'components/AddAndUpdateProspects/types';
import commonS from 'styles/common.module.less';
import useInsurance from 'hooks/useInsurance';
import useSettings from 'hooks/useSettings';
import { EnumFields } from 'types/enumerationData';
import { sortByProperty } from 'utils/form';
import { defaultIndexOf } from 'utils/clientEligibility';
import dayjs from 'utils/dayjs';
import FileUploadPrivate from 'components/form/FileUploadPrivate';
import BirthdayInput from 'components/form/BirthdayInput';
import s from './s.module.less';

type Props = {
    open: boolean;
    onClose: () => void;
    onSuccess: () => void;
    item?: Prospect;
    isGroupPractice?: boolean;
};

const POLICY_HOLDER_RELATIONSHIP_OPTIONS = [
    { label: 'Child', value: 'CHILD' },
    { label: 'Parent', value: 'PARENT' },
    { label: 'Spouse', value: 'SPOUSE' },
    { label: 'Other', value: 'OTHER' },
];

const createEmptyInsurance = (insuranceType: 'PRIMARY' | 'SECONDARY'): FormInsurance => ({
    insuranceType,
    policyHolderType: 'SELF',
    carrier: '',
    plan: '',
    memberId: '',
    groupId: '',
    front: null,
    backend: null,
});

const EditInsuranceModal = ({ open, onClose, onSuccess, item, isGroupPractice }: Props) => {
    const [submitting, setSubmitting] = React.useState(false);
    const [activeTab, setActiveTab] = React.useState<'PRIMARY' | 'SECONDARY'>('PRIMARY');
    const [primaryList, setPrimaryList] = React.useState<FormInsurance[]>([createEmptyInsurance('PRIMARY')]);
    const [secondaryList, setSecondaryList] = React.useState<FormInsurance[]>([]);

    const [setting] = useSettings();
    const stateOption = sortByProperty(setting[EnumFields.STATE] || [], 'label');
    const [allInsurance] = useInsurance();

    const targetState = React.useMemo(() => {
        if (!item?.state) return undefined;
        return stateOption?.find((i: Meta) => i.value === item.state || i.label === item.state);
    }, [item?.state, stateOption]);

    const stateCarrier = React.useMemo(
        () =>
            targetState
                ? allInsurance.find((ins) => ins.state === targetState.label || ins.state === targetState.value)
                : undefined,
        [targetState, allInsurance],
    );

    const carrierOptions: TCarrier[] = stateCarrier?.carriers || [];

    const getPlanOptions = (carrierName: string): string[] => {
        if (!carrierName) return [];
        return stateCarrier?.carriers.find((c) => c.name === carrierName)?.plans || [];
    };

    React.useEffect(() => {
        if (open && item?.insurances && item.insurances.length > 0) {
            const primaries: FormInsurance[] = [];
            const secondaries: FormInsurance[] = [];

            const sorted = [...item.insurances].sort((a, b) => (a.sort || 0) - (b.sort || 0));
            sorted.forEach((ins) => {
                const formIns: FormInsurance = {
                    id: ins.id,
                    insuranceType: ins.insuranceType || (ins.sort === 1 ? 'PRIMARY' : 'SECONDARY'),
                    isDefault: ins.isDefault,
                    policyHolderType: ins.policyHolderType || 'SELF',
                    relationship: ins.relationship,
                    holderFirstName: ins.holderFirstName,
                    holderLastName: ins.holderLastName,
                    holderDob:
                        ins.holderDob && ins.holderDob.includes('-')
                            ? dayjs(ins.holderDob, 'YYYY-MM-DD').format('MM/DD/YYYY')
                            : ins.holderDob,
                    holderGender: ins.holderGender
                        ? ['male', 'female'].includes(ins.holderGender)
                            ? ins.holderGender
                            : 'other'
                        : undefined,
                    firstName: ins.firstName,
                    lastName: ins.lastName,
                    carrier: ins.carrier,
                    plan: ins.plan,
                    memberId: ins.memberId,
                    groupId: ins.groupId,
                    front: ins.front,
                    backend: ins.backend,
                };

                if (formIns.insuranceType === 'PRIMARY') {
                    primaries.push(formIns);
                } else {
                    secondaries.push(formIns);
                }
            });

            setPrimaryList(primaries.length > 0 ? primaries : [createEmptyInsurance('PRIMARY')]);
            setSecondaryList(secondaries);
            setActiveTab('PRIMARY');
        } else if (open) {
            setPrimaryList([createEmptyInsurance('PRIMARY')]);
            setSecondaryList([]);
            setActiveTab('PRIMARY');
        }
    }, [open, item?.insurances]);

    const updateInsurance = (
        list: FormInsurance[],
        setList: React.Dispatch<React.SetStateAction<FormInsurance[]>>,
        index: number,
        field: Partial<FormInsurance>,
    ) => {
        const updated = [...list];
        updated[index] = { ...updated[index], ...field };
        setList(updated);
    };

    const addInsurance = (type: 'PRIMARY' | 'SECONDARY') => {
        if (type === 'PRIMARY') {
            setPrimaryList((prev) => [...prev, createEmptyInsurance('PRIMARY')]);
        } else {
            setSecondaryList((prev) => [...prev, createEmptyInsurance('SECONDARY')]);
        }
    };

    const removeInsurance = (type: 'PRIMARY' | 'SECONDARY', index: number) => {
        if (type === 'PRIMARY') {
            setPrimaryList((prev) => prev.filter((_, i) => i !== index));
        } else {
            setSecondaryList((prev) => prev.filter((_, i) => i !== index));
        }
    };

    const validateAll = (): boolean => {
        const allItems = [...primaryList, ...secondaryList];
        for (const ins of allItems) {
            if (!ins.carrier) {
                message.error('Insurance carrier is required');
                return false;
            }
            if (!ins.memberId) {
                message.error('Member ID is required');
                return false;
            }
            if (ins.policyHolderType === 'SOMEONE_ELSE') {
                if (!ins.relationship) {
                    message.error('Relationship is required');
                    return false;
                }
                if (!ins.holderFirstName || !ins.holderLastName) {
                    message.error('Holder name is required');
                    return false;
                }
                if (!ins.holderDob) {
                    message.error('Date of birth is required');
                    return false;
                }
                if (!ins.holderGender) {
                    message.error('Member gender is required');
                    return false;
                }
            }
        }
        return true;
    };

    const handleSubmit = async () => {
        if (!item) return;
        if (!validateAll()) return;

        const insurances: TProspectInsurance[] = [];
        let sort = 1;
        // 沿用加载时的 isDefault，不按位置重排（Set as default 只翻 isDefault 不动 sort）
        const primaryDefaultIdx = defaultIndexOf(primaryList);
        const secondaryDefaultIdx = defaultIndexOf(secondaryList);
        primaryList.forEach((ins, idx) => {
            const data: TProspectInsurance = {
                id: ins.id,
                carrier: ins.carrier,
                plan: ins.plan,
                memberId: ins.memberId,
                groupId: ins.groupId,
                front: ins.front,
                backend: ins.backend,
                sort: sort++,
                firstName: ins.firstName,
                lastName: ins.lastName,
                insuranceType: 'PRIMARY',
                isDefault: idx === primaryDefaultIdx,
                policyHolderType: ins.policyHolderType,
            };
            if (ins.policyHolderType === 'SOMEONE_ELSE') {
                data.relationship = ins.relationship;
                data.holderFirstName = ins.holderFirstName;
                data.holderLastName = ins.holderLastName;
                data.holderDob = ins.holderDob ? dayjs(ins.holderDob, 'MM/DD/YYYY').format('YYYY-MM-DD') : undefined;
                data.holderGender = ins.holderGender;
            }
            const original = item.insurances?.find((o) => o.id === ins.id);
            if (original) {
                if (data.front === original.front) data.front = null;
                if (data.backend === original.backend) data.backend = null;
            }
            insurances.push(data);
        });

        secondaryList.forEach((ins, idx) => {
            const data: TProspectInsurance = {
                id: ins.id,
                carrier: ins.carrier,
                plan: ins.plan,
                memberId: ins.memberId,
                groupId: ins.groupId,
                front: ins.front,
                backend: ins.backend,
                sort: sort++,
                firstName: ins.firstName,
                lastName: ins.lastName,
                insuranceType: 'SECONDARY',
                isDefault: idx === secondaryDefaultIdx,
                policyHolderType: ins.policyHolderType,
            };
            if (ins.policyHolderType === 'SOMEONE_ELSE') {
                data.relationship = ins.relationship;
                data.holderFirstName = ins.holderFirstName;
                data.holderLastName = ins.holderLastName;
                data.holderDob = ins.holderDob ? dayjs(ins.holderDob, 'MM/DD/YYYY').format('YYYY-MM-DD') : undefined;
                data.holderGender = ins.holderGender;
            }
            const original = item.insurances?.find((o) => o.id === ins.id);
            if (original) {
                if (data.front === original.front) data.front = null;
                if (data.backend === original.backend) data.backend = null;
            }
            insurances.push(data);
        });

        const prospectData: Prospect = {
            id: item.id,
            firstName: item.firstName,
            lastName: item.lastName,
            email: item.email,
            tel: item.tel,
            gender: item.gender,
            state: item.state,
            city: item.city,
            zip: item.zip,
            notes: item.notes,
            address: item.address,
            idFront: null,
            idBack: null,
            type: item.type,
            billing: item.billing,
            insurances,
        };

        setSubmitting(true);
        try {
            const result =
                isGroupPractice && item.identityId
                    ? await gpUpdateProspect(item.identityId, prospectData)
                    : await updateProspect(item.id!, prospectData, isGroupPractice ? item.providerId : undefined);

            if (result.error) {
                message.error(result.error);
            } else {
                message.success('Insurance information has been updated successfully');
                onSuccess();
            }
        } catch (e: unknown) {
            // eslint-disable-next-line no-console
            console.error(e);
        } finally {
            setSubmitting(false);
        }
    };

    const currentList = activeTab === 'PRIMARY' ? primaryList : secondaryList;
    const setCurrentList = activeTab === 'PRIMARY' ? setPrimaryList : setSecondaryList;
    const renderDeleteIcon = () => (
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
            <path
                d="M19 7L18.1327 19.1425C18.0579 20.1891 17.187 21 16.1378 21H7.86224C6.81296 21 5.94208 20.1891 5.86732 19.1425L5 7M10 11V17M14 11V17M15 7V4C15 3.44772 14.5523 3 14 3H10C9.44772 3 9 3.44772 9 4V7M4 7H20"
                stroke="#6B7280"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
            />
        </svg>
    );

    const renderInsuranceForm = (ins: FormInsurance, index: number) => {
        const update = (field: Partial<FormInsurance>) => updateInsurance(currentList, setCurrentList, index, field);
        const canDelete = activeTab === 'PRIMARY' ? primaryList.length > 1 : true;

        return (
            <div className={s.card} key={`${activeTab}-${index}`}>
                {(currentList.length > 1 || activeTab === 'SECONDARY') && (
                    <div className={s.cardHeader}>
                        <span className={s.cardTitle}>
                            {activeTab === 'PRIMARY' ? 'Primary' : 'Secondary'} insurance {index + 1}
                        </span>
                        {canDelete && (
                            <span className={s.deleteBtn} onClick={() => removeInsurance(activeTab, index)}>
                                {renderDeleteIcon()}
                            </span>
                        )}
                    </div>
                )}
                <div className={s.row}>
                    <div className={s.rowItem}>
                        <Form.Item label="Primary policy holder" required>
                            <Radio.Group
                                value={ins.policyHolderType}
                                onChange={(e) => {
                                    const val = e.target.value;
                                    update({
                                        policyHolderType: val,
                                        ...(val === 'SELF'
                                            ? {
                                                  relationship: undefined,
                                                  holderFirstName: undefined,
                                                  holderLastName: undefined,
                                                  holderDob: undefined,
                                                  holderGender: undefined,
                                              }
                                            : {}),
                                    });
                                }}
                            >
                                <Radio value="SELF">Client</Radio>
                                <Radio value="SOMEONE_ELSE">Someone else</Radio>
                            </Radio.Group>
                        </Form.Item>
                    </div>
                </div>
                {ins.policyHolderType === 'SOMEONE_ELSE' && (
                    <div className={s.someoneElseBox}>
                        <div className={s.row}>
                            <div className={s.rowItem}>
                                <Form.Item
                                    style={{ width: '300px' }}
                                    label="Client's relationship to the policy holder"
                                    required
                                >
                                    <Select
                                        value={ins.relationship}
                                        placeholder="Please select"
                                        onChange={(val) => update({ relationship: val })}
                                        options={POLICY_HOLDER_RELATIONSHIP_OPTIONS}
                                    />
                                </Form.Item>
                            </div>
                        </div>
                        {ins.relationship && (
                            <>
                                <div className={s.row}>
                                    <div className={s.rowItem}>
                                        <Form.Item label="Legal first name" required>
                                            <Input
                                                maxLength={25}
                                                value={ins.holderFirstName}
                                                onChange={(e) => update({ holderFirstName: e.target.value })}
                                            />
                                        </Form.Item>
                                    </div>
                                    <div className={s.rowItem}>
                                        <Form.Item label="Legal last name" required>
                                            <Input
                                                maxLength={25}
                                                value={ins.holderLastName}
                                                onChange={(e) => update({ holderLastName: e.target.value })}
                                            />
                                        </Form.Item>
                                    </div>
                                </div>
                                <div className={s.row}>
                                    <div className={s.rowItem}>
                                        <Form.Item label="Date of birth" required>
                                            <BirthdayInput
                                                value={ins.holderDob}
                                                onChange={(val) => update({ holderDob: val as string })}
                                            />
                                        </Form.Item>
                                    </div>
                                    <div className={s.rowItem}>
                                        <Form.Item label="Member gender" required>
                                            <Select
                                                value={ins.holderGender}
                                                onChange={(val) => update({ holderGender: val })}
                                                options={[
                                                    { label: 'Male', value: 'male' },
                                                    { label: 'Female', value: 'female' },
                                                    { label: 'Others', value: 'other' },
                                                ]}
                                            />
                                        </Form.Item>
                                    </div>
                                </div>
                            </>
                        )}
                    </div>
                )}
                {ins.policyHolderType === 'SELF' && (
                    <div className={s.row}>
                        <div className={s.rowItem}>
                            <Form.Item label="Member legal first name">
                                <Input
                                    maxLength={25}
                                    value={ins.firstName}
                                    onChange={(e) => update({ firstName: e.target.value })}
                                />
                            </Form.Item>
                        </div>
                        <div className={s.rowItem}>
                            <Form.Item label="Member legal last name">
                                <Input
                                    maxLength={25}
                                    value={ins.lastName}
                                    onChange={(e) => update({ lastName: e.target.value })}
                                />
                            </Form.Item>
                        </div>
                    </div>
                )}
                <div className={s.row}>
                    <div className={s.rowItem}>
                        <Form.Item label="Insurance carrier" required>
                            <AutoComplete
                                style={{ width: '100%' }}
                                options={carrierOptions.map((c) => ({ value: c.name }))}
                                value={ins.carrier}
                                placeholder="Please select or input"
                                filterOption={(inputValue, option) =>
                                    !!option?.value.toLowerCase().includes(inputValue.toLowerCase())
                                }
                                onChange={(val) => update({ carrier: val, plan: '' })}
                            >
                                <Input allowClear />
                            </AutoComplete>
                        </Form.Item>
                    </div>
                    <div className={s.rowItem}>
                        <Form.Item label="Insurance plan">
                            <AutoComplete
                                style={{ width: '100%' }}
                                options={getPlanOptions(ins.carrier).map((p) => ({ value: p }))}
                                value={ins.plan}
                                placeholder="Please select or input"
                                filterOption={(inputValue, option) =>
                                    !!option?.value.toLowerCase().includes(inputValue.toLowerCase())
                                }
                                onChange={(val) => update({ plan: val })}
                            >
                                <Input allowClear />
                            </AutoComplete>
                        </Form.Item>
                    </div>
                </div>
                <div className={s.row}>
                    <div className={s.rowItem}>
                        <Form.Item label="Member ID" required>
                            <Input
                                value={ins.memberId}
                                onChange={(e) => update({ memberId: e.target.value })}
                                allowClear
                            />
                        </Form.Item>
                    </div>
                    <div className={s.rowItem}>
                        <Form.Item label="Group ID">
                            <Input
                                value={ins.groupId}
                                onChange={(e) => update({ groupId: e.target.value })}
                                allowClear
                            />
                        </Form.Item>
                    </div>
                </div>
                <div className={s.row}>
                    <div className={s.rowItem}>
                        <Form.Item label="">
                            <FileUploadPrivate
                                accept="image/*"
                                uploadType="PROSPECT_INSURANCE"
                                notNeedSign
                                value={ins.front}
                                onChange={(val) => update({ front: val as string | null })}
                            />
                        </Form.Item>
                    </div>
                    <div className={s.rowItem}>
                        <Form.Item label="">
                            <FileUploadPrivate
                                accept="image/*"
                                uploadType="PROSPECT_INSURANCE"
                                notNeedSign
                                value={ins.backend}
                                onChange={(val) => update({ backend: val as string | null })}
                            />
                        </Form.Item>
                    </div>
                </div>
            </div>
        );
    };

    return (
        <Modal
            title="Edit insurance"
            open={open}
            onCancel={onClose}
            onOk={handleSubmit}
            okText="Save"
            okButtonProps={{ loading: submitting }}
            width={720}
            destroyOnClose
            className={commonS.modalFixHeightWrap}
        >
            <Form layout="vertical" className={commonS.formStyle1}>
                <div className={s.tabs}>
                    <div
                        className={s.tab}
                        style={activeTab === 'PRIMARY' ? { color: '#fff', background: '#006957' } : undefined}
                        onClick={() => setActiveTab('PRIMARY')}
                    >
                        Primary
                    </div>
                    <div
                        className={s.tab}
                        style={activeTab === 'SECONDARY' ? { color: '#fff', background: '#006957' } : undefined}
                        onClick={() => setActiveTab('SECONDARY')}
                    >
                        Secondary
                    </div>
                </div>
                <div className={s.cardWrap}>
                    {activeTab === 'SECONDARY' && secondaryList.length === 0 ? (
                        <div className={s.emptyState}>
                            <div className={s.emptyIllustration}>
                                <svg
                                    width="120"
                                    height="80"
                                    viewBox="0 0 120 80"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                >
                                    <rect
                                        x="10"
                                        y="5"
                                        width="100"
                                        height="70"
                                        rx="8"
                                        fill="#F3F4F6"
                                        stroke="#E5E7EB"
                                        strokeWidth="1"
                                    />
                                    <rect x="22" y="15" width="30" height="4" rx="2" fill="#D1D5DB" />
                                    <rect x="68" y="15" width="30" height="4" rx="2" fill="#D1D5DB" />
                                    <rect x="22" y="28" width="76" height="24" rx="6" fill="#E5E7EB" />
                                    <rect x="28" y="34" width="16" height="12" rx="3" fill="#3B82F6" />
                                    <rect x="30" y="38" width="12" height="4" rx="1" fill="white" />
                                    <rect x="50" y="34" width="24" height="3" rx="1.5" fill="#D1D5DB" />
                                    <rect x="50" y="40" width="18" height="3" rx="1.5" fill="#D1D5DB" />
                                    <rect x="22" y="58" width="40" height="3" rx="1.5" fill="#D1D5DB" />
                                    <rect x="22" y="65" width="28" height="3" rx="1.5" fill="#D1D5DB" />
                                </svg>
                            </div>
                            <span className={s.emptyTitle}>No secondary insurance</span>
                            <span className={s.emptySubtitle}>
                                Add one if the patient is covered by more than one plan.
                            </span>
                            <Button type="primary" onClick={() => addInsurance('SECONDARY')}>
                                Add secondary insurance
                            </Button>
                        </div>
                    ) : (
                        <>
                            {currentList.map((ins, idx) => renderInsuranceForm(ins, idx))}
                            <div className={s.addInsurance} onClick={() => addInsurance(activeTab)}>
                                + Add {activeTab.toLowerCase()} insurance
                            </div>
                        </>
                    )}
                </div>
            </Form>
        </Modal>
    );
};

export default EditInsuranceModal;
